/**
 * hearthRules — the arithmetic of the vigil, with no Phaser in it.
 *
 * These functions *are* the game: how fast the fire eats its fuel, how far the
 * lights carry, which of them wins on a given spot, whether a thing is close
 * enough to pick up, and how worn a gathering spot looks. Everything else in
 * the scene is presentation.
 *
 * They live apart from `HearthFire` and `ScenePropsSystem` for one blunt
 * reason: this package's test environment does not load Phaser (see
 * `vitest.config.ts` — "tests live next to Phaser-free modules only"), so any
 * rule written inside a class that imports Phaser is a rule that cannot be
 * tested. The whole of Phase 2's logic shipped that way, and a reviewer was
 * right to call it: 545 green tests, none of them touching the game.
 *
 * Keep new rules here and the renderers thin.
 */

// ── burning ────────────────────────────────────────────────────────────────

/**
 * Fuel a hearth opens with. Lives here rather than as a literal in HearthFire
 * so the economy tests can read the real number instead of transcribing it —
 * transcribed constants are how the economy comment came to be wrong three
 * times without a single test noticing.
 */
export const HEARTH_START_FUEL = 0.75;

/**
 * How fast a full fire eats its fuel, per second.
 *
 * At 0.010 an untended fire falls from full to embers in about a hundred
 * seconds — long enough that you are never scrambling, short enough that
 * walking to the far end of the grove and back is a decision rather than a
 * formality. The dark window of a vigil is 420 s, so this number sets the whole
 * economy; see docs/design/single-scene-game-spec.md → Phase 3.
 */
export const BURN_PER_SECOND = 0.010;

/**
 * A small fire burns slower than a big one — less surface, less draught. This
 * is the fraction of the full rate a fire at zero fuel would burn at, and it is
 * what stops the last embers from ever quite going out: the decay eases toward
 * a floor rather than running straight to nothing.
 */
export const BURN_FLOOR_FACTOR = 0.4;

/**
 * Fuel remaining after burning for `deltaMs`, clamped to 0..1.
 *
 * The rate scales with how much is burning, so a roaring fire is expensive to
 * hold and a low one costs almost nothing to keep — which is what makes
 * building it up a *choice* rather than a strictly-better move. Letting it sit
 * low is a legitimate way to play; it just means a smaller grove.
 */
export function burnFuel(fuel: number, deltaMs: number): number {
  const f = clamp01(fuel);
  const factor = BURN_FLOOR_FACTOR + (1 - BURN_FLOOR_FACTOR) * f;
  return clamp01(f - BURN_PER_SECOND * factor * (deltaMs / 1000));
}

// ── reach ──────────────────────────────────────────────────────────────────

/**
 * Minimum litness at which a fuel source can be taken from. Low on purpose: a
 * thing you can see at the edge of the firelight but cannot pick up is the most
 * annoying kind of boundary, so the cut sits close to the outer edge of what is
 * visible at all.
 */
export const GATHER_LIT_THRESHOLD = 0.12;

/**
 * How strongly a light at `distance` falls on something, 0..1, given the radius
 * over which it carries. Linear, and 0 beyond the radius.
 *
 * Called with two different radii for two different questions — see
 * `ScenePropsSystem.updateLighting`. Collapsing those into one number was the
 * bug that left the far half of the grove unreachable at every fire size.
 */
export function litnessAt(distance: number, radius: number): number {
  return clamp01(1 - distance / Math.max(radius, 1));
}

/**
 * A light falling on the scene, in whatever space the caller is working in.
 */
export interface RadialLight {
  x: number;
  y: number;
  /** How far it shades things. */
  tintRadius: number;
  /** How far you can still make something out by it. */
  reachRadius: number;
}

/**
 * Vertical distance counts for less. The scene is a shallow diorama seen from
 * slightly above, so a hand-span up the frame is further away in depth than the
 * same span across it — weighting y equally makes the far treeline read as lit
 * while the near ground goes dark.
 */
export const DIORAMA_Y_WEIGHT = 1.7;

/**
 * The best light any source throws on a point.
 *
 * Max, not sum. Two half-lit sources do not add up to a lit one — you either
 * have enough light to see by or you don't — and taking the max means carrying
 * the lantern somewhere can only ever open the grove up, never wash out the
 * hearth you left behind.
 */
export function bestLightAt(
  lights: ReadonlyArray<RadialLight>,
  x: number,
  y: number,
): { tint: number; reach: number } {
  let tint = 0;
  let reach = 0;
  for (const light of lights) {
    const dx = x - light.x;
    const dy = (y - light.y) * DIORAMA_Y_WEIGHT;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const t = litnessAt(dist, light.tintRadius);
    if (t > tint) tint = t;
    const r = litnessAt(dist, light.reachRadius);
    if (r > reach) reach = r;
  }
  return { tint, reach };
}

/** Is this lit well enough to walk over and pick up? */
export function canGather(litness: number): boolean {
  return litness >= GATHER_LIT_THRESHOLD;
}

// ── feeding ────────────────────────────────────────────────────────────────

/**
 * How much of an armful the fire must be able to use before it will take it.
 *
 * `setFuel` clamps at 1, so before this existed a fire at 0.99 would accept a
 * 0.20 armful and silently discard three quarters of it. Measured in a real
 * playthrough: a full gather beat plus the walk out and back returned +0.05.
 * The cost was real and the benefit was deleted, which the spec's checklist
 * calls a fake choice ("Respect for the player — no fake choices").
 *
 * At a half, a big 0.42 log needs the fire at or below 0.79 — you do not stack
 * a log on a roaring fire — while a 0.13 handful of kindling can top one off to
 * 0.935. That is roughly what the objects themselves suggest, which is the test
 * for a rule nobody is ever going to be shown.
 */
export const MIN_USEFUL_FEED_FRACTION = 0.5;

/**
 * Can the fire take this armful without most of it going to waste?
 *
 * When it cannot, the answer is that she keeps hold of it — NOT that the click
 * is refused. A silent refusal would be worse than the silent waste it
 * replaces. She walks over, the fire is plainly roaring, and she is plainly
 * still holding the wood: that is the "what you carry" row of the scene's
 * interface table doing the explaining, with nothing added to the screen.
 */
export function hasRoomFor(fuel: number, amount: number): boolean {
  // The epsilon is not decoration. `1 - 0.79` is 0.20999999999999996 while
  // `0.42 * 0.5` is 0.21, so a fire sitting exactly on the documented cut
  // refused a log by 2.8e-17 — the rule says "at least half" and the code has
  // to mean it. This is the third float-boundary slip of its kind in this
  // codebase; comparisons against a derived threshold need slack.
  return 1 - clamp01(fuel) >= amount * MIN_USEFUL_FEED_FRACTION - 1e-9;
}

// ── extraction ─────────────────────────────────────────────────────────────

/**
 * How long it takes to work one armful loose, in ms.
 *
 * This is the whole of Phase 3. The economy was written as a trade of fuel
 * against *time away from the fire*, and it never was one: the walker crosses
 * the two-screen world in about seven seconds, so the far stump cost 0.045 fuel
 * to gain 0.28 and the near log was practically free. Nothing was scarce, so
 * nothing was a decision — which is the exact diagnosis this whole prototype
 * exists to answer.
 *
 * Time is the night's real currency, and this is the only thing that spends it.
 * The eleven armfuls in the grove come to 28.6 s of work on top of the walking,
 * and the fire burns through all of it. It attaches to *extraction* and nothing
 * else:
 * looking, listening, walking and standing still remain free, permanently,
 * which is the other half of the rule (see the spec's "The economy" — time
 * pressure measurably converts looking into glancing, so a game about stopping
 * to look must never tax stopping to look).
 */
export const GATHER_WORK_MS = 2600;

/**
 * Where a gather is up to, 0..1. Nothing renders this as a bar — the pile
 * itself rocks while she works it, and that is the whole readout.
 */
export function gatherProgress(elapsedMs: number): number {
  return clamp01(elapsedMs / GATHER_WORK_MS);
}

// ── wear ───────────────────────────────────────────────────────────────────

/**
 * Scale multiplier for a gathering spot with `remaining` of `total` armfuls
 * left. Down to 55% by the last one — obvious at a glance, but never so small
 * it reads as a different object.
 *
 * This is how the state of the clearing is legible without a number on screen:
 * the deadfall visibly gets picked over as the night goes on.
 */
export function wearScale(remaining: number, total: number): number {
  if (total <= 0) return 1;
  const left = clamp01(remaining / total);
  return 0.55 + 0.45 * left;
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
